import toast from '@components/Common/Toast.js';
import { LinkIcon, PlusIcon, XIcon } from '@icons/IconLibrary';
import { UIDiv, UIInput, UIText } from '@ui';
import { updateColumnValue } from '@utils/columnUtils.js';

export default class LinkCell extends UIDiv {
  constructor(
    column,
    item = {},
    value = '',
    canEdit = true,
    onValueChange = null,
    setCellEditingState = null,
  ) {
    console.log('LinkCell constructor called');
    console.log({
      column,
      item,
      value,
      canEdit,
      onValueChange,
      setCellEditingState,
    });
    super();

    this.column = column;
    this.item = item;
    this.value = value;
    this.isEditMode = false;
    this.canEdit = canEdit;
    this.onValueChange = onValueChange; // Callback for value changes instead of API calls
    this.setCellEditingState = setCellEditingState; // Reference to parent row for edit state notification
    this.render();
  }

  // Validate if string is a valid URL
  isValidUrl(string) {
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      // If URL doesn't have protocol, try adding https://
      try {
        const url = new URL(`https://${string}`);
        return true;
      } catch (_) {
        return false;
      }
    }
  }

  // Ensure URL has protocol
  formatUrl(url) {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  }

  render() {
    this.clear();

    // If the cell is in edit mode, render the input
    if (!this.canEdit) {
      this.renderUneditableInput();
      return;
    }

    if (this.value.trim() === '') {
      this.renderEmptyState();
    } else {
      this.renderLinkState();
    }
  }

  renderEmptyState() {
    const emptyContainer = new UIDiv();
    emptyContainer.addClass(
      'group hover:border hover:border-solid hover:border-gray-300 border border-dashed border-border hover:bg-gray-50 rounded-md min-h-[34px] cursor-text',
    );

    // Hover state: show LinkIcon and plus
    const hoverContent = new UIDiv();
    hoverContent.addClass(
      'p-1 px-2 hidden group-hover:!flex items-center gap-1.5',
    );

    const linkIcon = new LinkIcon({
      size: 18,
      color: 'none',
    });
    const plusIcon = new PlusIcon({
      size: 12,
      className: 'bg-[var(--o-hive-primary-80)] rounded-full',
      strokeColor: 'white',
    });
    hoverContent.add(linkIcon, plusIcon);
    emptyContainer.add(hoverContent);

    emptyContainer.setAttribute('tabindex', '0');
    emptyContainer.onKeyDown(event => {
      if (event.key === 'Enter' && !this.isEditMode) {
        event.preventDefault();
        this.enterEditMode();
      }
    });
    // Click handler to enter edit mode
    emptyContainer.onClick(() => {
      this.enterEditMode();
    });

    this.add(emptyContainer);
  }

  renderLinkState() {
    const linkContainer = new UIDiv();
    linkContainer.addClass(
      'group relative hover:border hover:border-gray-300 hover:bg-gray-50 rounded-md min-h-[34px] flex items-center gap-0.5 px-2',
    );

    const isValid = this.isValidUrl(this.value);
    const formattedUrl = this.formatUrl(this.value);

    // Create clickable link
    const linkElement = document.createElement('a');
    linkElement.href = isValid ? formattedUrl : '#';
    linkElement.target = '_blank';
    linkElement.rel = 'noopener noreferrer';
    linkElement.className =
      'truncate flex-1 text-[var(--o-hive-primary-80)] hover:underline cursor-pointer';
    linkElement.textContent = this.value;

    // Prevent link click from entering edit mode
    linkElement.addEventListener('click', event => {
      if (!isValid) {
        event.preventDefault();
        this.enterEditMode();
      } else {
        event.stopPropagation();
      }
    });

    linkContainer.dom.appendChild(linkElement);

    // Click handler on container background to enter edit mode
    linkContainer.onClick(event => {
      // Only enter edit mode if clicking on the container, not the link
      if (event.target === linkContainer.dom) {
        this.enterEditMode();
      }
    });

    linkContainer.setAttribute('tabindex', '0');
    linkContainer.onKeyDown(event => {
      if (event.key === 'Enter' && !this.isEditMode) {
        event.preventDefault();
        this.enterEditMode();
      }
    });

    // Hover show clear icon on right
    const clearIcon = new XIcon({
      size: 12,
      className:
        'absolute right-2 top-1/2 transform -translate-y-1/2 hidden group-hover:!flex justify-center items-center bg-gray-200 hover:bg-gray-300 rounded-md cursor-pointer !w-5 !h-5',
    });
    clearIcon.onClick(async event => {
      event.stopPropagation(); // Prevent triggering the click handler
      try {
        // If onValueChange callback is provided, use it instead of API call
        if (this.onValueChange) {
          this.onValueChange('');
          // Update the local value
          this.value = '';
          this.exitEditMode();
        } else {
          // Update the value via API if we have the necessary information
          const entityType = !this.item.reconstructions ? 'folder' : 'file';
          const entityId = this.item.id || this.item.sharingId;

          await updateColumnValue(this.column.id, entityType, entityId, '');

          // Update the local value
          this.value = '';
          if (this.item) {
            this.item[this.column.key] = '';
          }
          this.exitEditMode();
        }
      } catch (error) {
        console.error('Failed to update column value:', error);
        toast.error(i18next.t('pages.projects.toast.failed_column_update'));
      }
    });
    linkContainer.add(clearIcon);

    this.add(linkContainer);
  }

  enterEditMode() {
    if (this.isEditMode) return;

    this.isEditMode = true;

    // Notify parent row that this cell is entering edit mode
    if (typeof this.setCellEditingState === 'function') {
      this.setCellEditingState(true);
    }

    this.clear();

    // Create input that fills the cell
    const input = new UIInput(this.value);
    input.setType('url');
    input.setAttribute('placeholder', 'https://example.com');
    input.addClass(
      'w-full min-h-[34px] cursor-text rounded-md px-2 focus:outline-none focus:!ring-2 focus:!ring-[var(--o-hive-primary-80)]',
    );

    this.add(input);

    // Focus the input and select all text
    setTimeout(() => {
      input.dom.focus();
      if (this.value) {
        input.dom.select();
      }
    }, 0);

    let isEditingSaved = false;

    // Handle clicking outside the cell to exit edit mode
    const handleClickOutside = event => {
      if (isEditingSaved) return;

      // Check if the click is outside this cell
      if (!this.contains(event.target)) {
        document.removeEventListener('click', handleClickOutside);
        saveEdit(event);
      }
    };

    // Add the click outside listener with a slight delay to avoid immediate triggering
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    // Handle save on Enter or blur
    const saveEdit = async event => {
      if (isEditingSaved) return;
      isEditingSaved = true;

      // Clean up the click outside listener
      document.removeEventListener('click', handleClickOutside);

      const newValue = input.getValue().trim();
      if (newValue === this.value) {
        this.exitEditMode();
        return;
      }

      try {
        // If onValueChange callback is provided, use it instead of API call
        if (this.onValueChange) {
          this.onValueChange(newValue, event);
          // Update the local value
          this.value = newValue;
          this.exitEditMode();
        } else {
          // Update the value via API if we have the necessary information
          const entityType = !this.item.reconstructions ? 'folder' : 'file';
          const entityId = this.item.id || this.item.sharingId;

          await updateColumnValue(
            this.column.id,
            entityType,
            entityId,
            newValue,
          );

          // Update the local value
          this.value = newValue;
          if (this.item) {
            this.item[this.column.key] = newValue;
          }

          this.exitEditMode();
        }
      } catch (error) {
        toast.error(i18next.t('pages.projects.toast.failed_column_update'));
        console.error('Failed to update column value:', error);
        // Revert to original value on error
        this.exitEditMode();
      }
    };

    // Handle cancel on Escape
    const cancelEdit = event => {
      if (event.key === 'Escape') {
        if (isEditingSaved) return;
        isEditingSaved = true;
        this.exitEditMode();
      }
    };

    // Add event listeners
    input.onKeyDown(event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        saveEdit(event);
      } else if (event.key === 'Escape') {
        cancelEdit(event);
      }
    });

    input.onBlur(saveEdit);
  }

  exitEditMode() {
    this.isEditMode = false;

    // Notify parent row that this cell is exiting edit mode
    if (typeof this.setCellEditingState === 'function') {
      this.setCellEditingState(false);
    }

    this.render();
  }

  renderUneditableInput() {
    const linkContainer = new UIDiv();
    linkContainer.addClass('min-h-[34px] flex items-center truncate px-2');

    const isValid = this.isValidUrl(this.value);
    const formattedUrl = this.formatUrl(this.value);

    if (this.value && isValid) {
      // Create clickable link
      const linkElement = document.createElement('a');
      linkElement.href = formattedUrl;
      linkElement.target = '_blank';
      linkElement.rel = 'noopener noreferrer';
      linkElement.className =
        'truncate flex-1 text-[var(--o-hive-primary-80)] hover:underline cursor-pointer';
      linkElement.textContent = this.value;

      linkContainer.dom.appendChild(linkElement);
    } else {
      // If invalid or empty, just show text
      const textContent = new UIText(this.value);
      textContent.addClass('truncate flex-1');
      linkContainer.add(textContent);
    }

    this.add(linkContainer);
  }
}
